/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import $ from 'jquery';

/**
 * The Visualization image capture controller is used to interact with the
 * liveWidget view and event handlers.
 **/
class VisImageCaptureController {

	/**
	 * Constructor
	 *
	 * @param {VisImageCaptureFeature} feature - Visualization image capture instance
	 * @param {WidgetAPI} widgetApi - widget API
	 * @param {DashboardAPI} dashboardApi - dashboard API
	 */
	constructor(feature, widgetApi, dashboardApi) {
		this.widgetApi = widgetApi;
		this.dashboardApi = dashboardApi;
		this.feature = feature;

		// any callbacks registered with activate will be called each thumbnail generation
		this._resetCaptureHandlers();

		//Internal handlers
		this._widgetApiHandlers = {
			onRenderStart: null,
			onRenderComplete: null
		};

	}

	_resetCaptureHandlers() {
		this._captureHandlers = {
			success: [],
			error: [],
			warning: [],
			clear: [],
			relink: []
		};
	}

	/**
	 * Activate controller
	 *
	 * @param [options] - an object that contains (currently) 1 callback  'cbOnThumbnailGenerated'
	 */
	activate(options = {}) {

		if (options.captureHandlers) {
			if (options.captureHandlers.success) {
				this.registerSuccessCaptureHandler(options.captureHandlers.success);
			}
			if (options.captureHandlers.error) {
				this.registerErrorCaptureHandler(options.captureHandlers.error);
			}
			if (options.captureHandlers.warning) {
				this.registerWarningCaptureHandler(options.captureHandlers.warning);
			}
			if (options.captureHandlers.clear) {
				this.registerClearCaptureHandler(options.captureHandlers.clear);
			}
			if (options.captureHandlers.relink) {
				this.registerRelinkCaptureHandler(options.captureHandlers.relink);
			}
		}
		const canvas = this.dashboardApi.getCanvas();
		const content = canvas && canvas.getContent(this.widgetApi.getId());
		const state = content && content.getFeature('state');
		state && state.whenStatusChanges(state.STATUS.RENDERED)
			.then(() => {
				const initialRenderInfo = {
					isInitial: true,
					isResizing: false
				};
				this.onRenderStart(initialRenderInfo);
				this.onRenderComplete(initialRenderInfo);

				if (!this._widgetApiHandlers.onRenderStart) {
					//todo: no equivalent new api for this.widgetApi.on;
					this._widgetApiHandlers.onRenderStart = this.widgetApi.on('renderStart', this.onRenderStart, this);
				}
				if (!this._widgetApiHandlers.onRenderComplete) {
					//todo: no equivalent new api for this.widgetApi.on;
					this._widgetApiHandlers.onRenderComplete = this.widgetApi.on('renderComplete', this.onRenderComplete, this);
				}
			});

		this.dashboardApi.getDashboardSvc('.LifeCycleManager')
			.then(LifeCycleManager => {
				LifeCycleManager.registerLifeCycleHandler('pre:relink', this.onRelinkStart.bind(this));
			});
	}

	/**
	 * Deactivate controller
	 */
	deactivate() {
		if (this._widgetApiHandlers.onRenderComplete) {
			this._widgetApiHandlers.onRenderComplete.remove();
			this._widgetApiHandlers.onRenderComplete = null;
		}

		// clear out the capture handlers
		this._resetCaptureHandlers();
		return Promise.resolve();
	}

	/**
	 * Destructor
	 */
	destroy() {
		this._onThumbnailGeneratedHandlers = [];
		this.deactivate();
	}

	/**
	 * Register capture handlers
	 *
	 * @param {Function} [successCallback=null] - success capture handler
	 * @param {Function} [errorCallback=null] - error capture handler
	 */
	registerCaptureHandlers(successCallback = null, errorCallback = null, warningCallback = null) {
		if (successCallback) {
			this.registerSuccessCaptureHandler(successCallback);
		}
		if (errorCallback) {
			this.registerErrorCaptureHandler(errorCallback);
		}
		if (warningCallback) {
			this.registerWarningCaptureHandler(warningCallback);
		}
	}

	/**
	 * Register success capture handler
	 *
	 * @param {Function} callback - success callback handler to register
	 */
	registerSuccessCaptureHandler(callback) {
		this._captureHandlers.success.push(callback);
	}

	/**
	 * Register waring capture handler
	 * @param {Function} callback - warning callback handler to register
	 */
	registerWarningCaptureHandler(callback) {
		this._captureHandlers.warning.push(callback);
	}

	/**
	 * Register error capture handler
	 *
	 * @param {Function} callback - error callback handler to register
	 */
	registerErrorCaptureHandler(callback) {
		this._captureHandlers.error.push(callback);
	}

	/**
	 * Register clear capture handler
	 *
	 * @param {Function} callback - clear callback handler to register
	 */
	registerClearCaptureHandler(callback) {
		this._captureHandlers.clear.push(callback);
	}

	/**
	 * Register relink capture handler
	 *
	 * @param {Function} callback - clear callback handler to register
	 */
	registerRelinkCaptureHandler(callback) {
		this._captureHandlers.relink.push(callback);
	}

	/**
	 * Call all previously registered success capture handlers
	 */
	callSuccessCaptureHandlers(result) {
		this._captureHandlers.success.forEach(callback => callback(result));
	}

	/**
	 * Call all previously registered error capture handlers
	 */
	callErrorCaptureHandlers(error) {
		this._captureHandlers.error.forEach(callback => callback(error));
	}

	/**
	 * Call all previously registered warning capture handlers
	 */
	callWarningCaptureHandlers(warning = { message: 'Widget has a warning.' }) {
		this._captureHandlers.warning.forEach(callback => callback(warning));
	}

	/**
	 * Call all previously registered clear capture handlers
	 */
	callClearCaptureHandlers() {
		this._captureHandlers.clear.forEach(callback => callback());
	}

	/**
	 * Call all previously registered clear capture handlers
	 */
	callRelinkCaptureHandlers() {
		this._captureHandlers.relink.forEach(callback => callback());
	}

	/**
	 * Check whether a specific renderInfo needs regeneration
	 *
	 * @param {object} renderInfo - rendering information
	 * @param {boolean} [renderInfo.isResizing] - boolean to signal whether renderComplete happened as a result of a resize or not
	 *
	 * @return {boolean} TRUE if needs re-generation and FALSE if not
	 */
	needsReGeneration(renderInfo) {
		return !renderInfo.isResizing;
	}

	/**
	 * Check whether a specific renderInfo needs clearing
	 *
	 * @param {object} renderInfo - render information
	 * @param {boolean} [renderInfo.isInitial] - boolean to signal whether renderStart happened as a result of an initial render or not
	 * @param {boolean} [renderInfo.isResizing] - boolean to signal whather renderStart happened as a result of a resize or not
	 *
	 * @return {boolean} TRUE if needs clearing or not
	 */
	needsClearing(renderInfo) {
		return !renderInfo.isResizing && !renderInfo.isInitial;
	}

	/**
	 * Handler called on widget render start
	 *
	 * @param {object} [renderInfo={}] - render information
	 * @param {boolean} [renderInfo.isInitial] - boolean to signal whether renderStart happened as a result of an initial render or not
	 * @param {boolean} [renderInfo.isResizing] - boolean to signal whather renderStart happened as a result of a resize or not
	 */
	onRenderStart(renderInfo = {}) {
		//todo: no equivalent new api;
		if (this.widgetApi.hasError() || this.needsClearing(renderInfo)) {
			this.callClearCaptureHandlers();
		}
	}

	/**
	 * Handler called on widget render complete
	 *
	 * @param {object} [renderInfo={}] - render information
	 * @param {boolean} [renderInfo.isResizing=false] - boolean to signal whether renderComplete happened as a result of a resize or not
	 *
	 * @return {Promise} Promise that is resolved or rejected based on the success of the image capture
	 */
	onRenderComplete(renderInfo = {}) {
		let promise;
		//todo: no equivalent new api;
		if (this.widgetApi.hasError()) {
			this.callErrorCaptureHandlers(new Error('Widget has error'));
			promise = Promise.resolve();
			//todo: no equivalent new api;
		} else if (this.widgetApi.hasWarning()) {
			this.callWarningCaptureHandlers();
			promise = Promise.resolve();
		} else if (this.needsReGeneration(renderInfo)) {
			this.timeout && clearTimeout(this.timeout);
			promise = this.delay()
				.then(() => this._generateCapture())
				.then(result => this.callSuccessCaptureHandlers(result))
				.catch((error) => this.callErrorCaptureHandlers(error));
		} else {
			promise = Promise.resolve();
		}
		return promise;
	}

	// introduce a setTimeout to slightly delay thumbnail generation to allow explore's properties panel to finish rendering
	delay = (time = 300) => {
		return new Promise((resolve) => {
			this.timeout = setTimeout(resolve, time);
		});
	}

	/**
	 * Handler called right before relink process starts
	 */
	onRelinkStart() {
		this.callRelinkCaptureHandlers();
	}

	_generateCapture() {

		const thumbnailConfig = this.dashboardApi.getAppConfig('thumbnail');

		if (!thumbnailConfig) {
			return Promise.resolve();
		}
		//todo: no equivalent new layoutAPI;
		const layoutApi = this.widgetApi.getLayoutAPI();

		const {
			width: widthRatio,
			height: heightRatio
		} = layoutApi.getSelfRatio();

		const {
			size: {
				width: widthConfig,
				height: heightConfig
			},
			type = 'svg'
		} = thumbnailConfig;

		// 244560: since we cant properly scale svg using viewBox(+ some other attribute) as it crashes IE11,
		// and we apply 8px padding on each side within thumbnail, we request an image deducting 16 to account for padding.
		// When VIDA identifies which attribute(in concert with viewBox) causes IE11 to crash, we can remove deduction of 16px
		// from thumbnail dimentions below and use viewBox to scale SVG correctly.
		const imageWidth = (widthConfig * widthRatio)-16;
		const imageHeight = (heightConfig * heightRatio)-16;

		const size = {
			width: imageWidth,
			height: imageHeight
		};
		//todo: no equivalent new APIs.
		return this.widgetApi.getVisApi()
			.generateThumbnail({
				size,
				type
			})
			.then(result => {
				let res;

				let thumbnail;
				if (typeof result === 'object') {
					thumbnail = result.thumbnail;
				} else {
					thumbnail = result;
				}

				if (thumbnail) {
					// for now, we'll pre-process the thumbnail and remove it manually,
					// but there is a fix coming, whenever that is fixed, this hack can be removed
					// more info: RTC 217310
					const content = $(thumbnail);
					const wrappedContent = $('<div>').append(content);
					content.first().removeAttr('class').attr('class', 'visThumbnail');
					res = wrappedContent.html();
				}
				return {
					...(typeof result === 'object' ? result : {}),
					thumbnail: res
				};
			});
	}
}

export default VisImageCaptureController;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-image-capture/src/js/VisImageCaptureController.js